import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService, AuthUser } from '../services/authService';
import { tenantRedirect } from '../lib/tenant';

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  login: (login: string, password: string) => Promise<void>;
  logout: () => void;
  updateUser: (patch: Partial<AuthUser>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(() => authService.getUser());

  // Sahifa ochilganda foydalanuvchini serverdan yangilash (/auth/me) —
  // avatar/rol o'zgarishlari qayta login qilmasdan aks etadi.
  // `/ilova` da EMAS: u yerda qurilma sessiyasi almashmoqda (`POST /qurilma/almash`). Eski `X-Qurilma`
  // bilan ketgan /auth/me serverga yangi sessiyadan keyin yetib 401 olardi va har qayta ochishda
  // foydalanuvchini /login ga chiqarib yuborardi (spec 2026-10-15 §6.2.7).
  useEffect(() => {
    if (!authService.getToken() || window.location.pathname.startsWith('/ilova')) return;
    authService.me().then((u) => { if (u) setUser(u); }).catch(() => { /* 401 — api interceptor login'ga yo'naltiradi */ });
  }, []);

  // SaaS: foydalanuvchini o'z maktabi subdomeniga yumshoq yo'naltirish (asosiy domen ostida)
  useEffect(() => { tenantRedirect(user); }, [user]);

  const login = async (login: string, password: string) => {
    const { user } = await authService.login(login, password);
    setUser(user);
  };

  const logout = () => {
    authService.logout();
    setUser(null);
  };

  const updateUser = (patch: Partial<AuthUser>) => {
    setUser(authService.patchUser(patch));
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: !!user, login, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
